import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { AccountMe } from '../types/accountMe'

const ME_REFRESH_EVENT = 'pixeloop:me-refresh'

export type Me = AccountMe & {
  phone_verified?: boolean
  wallet_linked?: boolean
  stripe_connect_ready?: boolean
  seller_eligibility?: {
    can_list: boolean
    can_publish: boolean
    can_withdraw: boolean
    requirements: {
      email_verified: boolean
      phone_verified: boolean
      wallet_linked: boolean
      stripe_connect_ready: boolean
    }
    blockers: Array<{ code: string; message: string }>
  }
  profile: {
    public_id?: string | null
    display_name?: string
    handle?: string
    avatar_url?: string
    profile_visibility_status?: string
    profile_visibility_message?: string
    user_safe_message?: string | null
    last_moderation_event_id?: string | null
    last_moderation_decision_action?: string | null
  }
}

export function useMe(enabled: boolean) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<Me>('/api/me/')
      .then((res) => {
        if (!cancelled) setMe(res.data)
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to fetch profile')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, version])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onRefresh = () => setVersion((v) => v + 1)
    window.addEventListener(ME_REFRESH_EVENT, onRefresh as EventListener)
    return () => window.removeEventListener(ME_REFRESH_EVENT, onRefresh as EventListener)
  }, [])

  const refresh = () => setVersion((v) => v + 1)
  return { me, loading, error, refresh }
}

export function requestMeRefresh() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(ME_REFRESH_EVENT))
}
